import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { DashboardLayout } from "../components/DashboardLayout";
import { formatApiError } from "../lib/formatApiError";
import { API } from "../lib/constants";

function ExportButtons({ content, title }) {
  const [exporting, setExporting] = useState(null);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const response = await axios.post(`${API}/content/export`, { content, title, format }, { responseType: "blob" });
      const ext = format === "md" ? "md" : format;
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `content.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* */ } finally {
      setExporting(null);
    }
  };

  return (
    <div className="flex gap-1.5">
      {[
        { fmt: "pdf", label: "PDF" },
        { fmt: "docx", label: "DOCX" },
        { fmt: "md", label: "MD" },
        { fmt: "txt", label: "TXT" }
      ].map(({ fmt, label }) => (
        <button key={fmt} onClick={() => handleExport(fmt)} disabled={exporting === fmt} className="text-xs bg-gray-100 hover:bg-gray-200 px-2.5 py-1 rounded font-medium transition-colors disabled:opacity-50" data-testid={`export-${fmt}`}>
          {exporting === fmt ? "..." : label}
        </button>
      ))}
    </div>
  );
}

export default function TextGenerator() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("template");

  const [contentType, setContentType] = useState("blog");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("professional");
  const [context, setContext] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [language, setLanguage] = useState(user?.preferred_language || "en");
  const [languages, setLanguages] = useState([]);

  // Template mode state
  const [template, setTemplate] = useState(null);
  const [templateVars, setTemplateVars] = useState({});

  const loadTemplate = useCallback(async (id) => {
    try {
      const { data } = await axios.get(`${API}/content/templates/${id}`);
      setTemplate(data);
      setContentType(data.content_type || "blog");
      const vars = {};
      data.variables.forEach((v) => { vars[v.name] = ""; });
      setTemplateVars(vars);
    } catch {
      setTemplate(null);
    }
  }, []);

  const loadLanguages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/content/languages`);
      setLanguages(data);
    } catch { /* */ }
  }, []);

  useEffect(() => {
    if (templateId) loadTemplate(templateId);
  }, [templateId, loadTemplate]);

  useEffect(() => { loadLanguages(); }, [loadLanguages]);

  const clearTemplate = () => {
    setTemplate(null);
    setTemplateVars({});
    window.history.replaceState({}, "", "/dashboard/text");
  };

  const handleGenerate = async () => {
    if (template) {
      const emptyVars = template.variables.filter((v) => !templateVars[v.name]?.trim());
      if (emptyVars.length > 0) {
        setError(`Please fill in: ${emptyVars.map((v) => v.label).join(", ")}`);
        return;
      }
    } else if (!topic.trim()) {
      setError("Please enter a topic");
      return;
    }

    setError("");
    setLoading(true);
    setResult("");
    try {
      const payload = template
        ? {
            content_type: template.content_type || "blog",
            topic: templateVars.topic || template.name,
            tone,
            additional_context: context,
            template_id: template.id,
            template_variables: templateVars,
            language
          }
        : {
            content_type: contentType,
            topic,
            tone,
            additional_context: context,
            language
          };
      const { data } = await axios.post(`${API}/content/generate-text`, payload);
      setResult(data.content);
      refreshUser();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              {template ? template.name : "Text Generator"}
            </h1>
            <p className="text-gray-600">
              {template ? template.description : "Create compelling content with GPT-5.2"}
            </p>
          </div>
          {!template && (
            <Link to="/dashboard/templates" className="text-sm text-[#0066FF] font-bold hover:underline" data-testid="browse-templates-link">
              Browse Templates
            </Link>
          )}
        </div>

        {template && (
          <div className="bg-[#0066FF]/5 border border-[#0066FF]/20 rounded-xl p-4 mb-6 flex items-center justify-between" data-testid="template-banner">
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-[#0066FF] text-white text-xs font-bold rounded-full">{template.category}</span>
              <span className="text-sm font-medium">Using template: <strong>{template.name}</strong></span>
            </div>
            <button onClick={clearTemplate} className="text-sm text-gray-500 hover:text-red-500 font-medium" data-testid="clear-template">
              Switch to Free-form
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white border border-black p-6 rounded-xl">
            <h2 className="text-lg font-bold mb-4">{template ? "Fill in the Details" : "Configure Your Content"}</h2>
            {error && <div className="bg-red-50 border border-red-500 text-red-700 p-3 mb-4 text-sm rounded-lg" data-testid="text-error">{error}</div>}
            <div className="space-y-4">
              {template ? (
                /* Template mode: show template variables */
                <>
                  {template.variables.map((v) => (
                    <div key={v.name}>
                      <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">{v.label}</label>
                      <input
                        type="text"
                        value={templateVars[v.name] || ""}
                        onChange={(e) => setTemplateVars({ ...templateVars, [v.name]: e.target.value })}
                        placeholder={v.placeholder}
                        className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg"
                        data-testid={`template-var-${v.name}`}
                      />
                    </div>
                  ))}
                </>
              ) : (
                /* Free-form mode */
                <>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Content Type</label>
                    <select value={contentType} onChange={(e) => setContentType(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" data-testid="content-type-select">
                      <option value="blog">Blog Post</option>
                      <option value="ad">Advertisement</option>
                      <option value="social">Social Media</option>
                      <option value="email">Email</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Topic / Subject</label>
                    <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g., Benefits of remote work" className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" data-testid="topic-input" />
                  </div>
                </>
              )}
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Tone</label>
                <select value={tone} onChange={(e) => setTone(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" data-testid="tone-select">
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="humorous">Humorous</option>
                  <option value="formal">Formal</option>
                  <option value="persuasive">Persuasive</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Language</label>
                <select value={language} onChange={(e) => setLanguage(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" data-testid="language-select">
                  {languages.length > 0 ? languages.map((l) => (
                    <option key={l.code} value={l.code}>{l.name}</option>
                  )) : <option value="en">English</option>}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Additional Context (Optional)</label>
                <textarea value={context} onChange={(e) => setContext(e.target.value)} placeholder="Add any specific requirements or context..." rows={3} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none resize-none rounded-lg" data-testid="context-input" />
              </div>
              {user?.brand_voice?.name && (
                <div className="bg-[#0066FF]/5 border border-[#0066FF]/20 rounded-lg p-3 flex items-center gap-2" data-testid="brand-voice-indicator">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  <span className="text-sm text-gray-600">Brand voice active: <strong>{user.brand_voice.name}</strong></span>
                </div>
              )}
              <div className="flex items-center justify-between pt-4">
                <span className="text-sm text-gray-500">Cost: 1 credit | Available: {user?.credits || 0}</span>
                <button onClick={handleGenerate} disabled={loading || (user?.credits || 0) < 1} className="bg-[#0066FF] text-white px-6 py-3 font-bold hover:bg-[#0052CC] transition-colors disabled:opacity-50 rounded-lg" data-testid="generate-text-btn">
                  {loading ? "Generating..." : "Generate Content"}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white border border-black p-6 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Generated Content</h2>
              {result && (
                <div className="flex items-center gap-3">
                  <ExportButtons content={result} title={topic || template?.name || "Generated Content"} />
                  <button onClick={() => navigator.clipboard.writeText(result)} className="text-sm text-[#0066FF] font-bold hover:underline" data-testid="copy-text-btn">Copy</button>
                </div>
              )}
            </div>
            <div className="min-h-[400px] border border-gray-200 p-4 bg-gray-50 rounded-lg" data-testid="text-result">
              {loading ? (
                <div className="flex items-center justify-center h-full"><div className="animate-pulse text-gray-400">Generating your content...</div></div>
              ) : result ? (
                <div className="whitespace-pre-wrap">{result}</div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">Your generated content will appear here</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
